import React from "react";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { useIntl } from "react-intl";
import IndicatorActions from "../../IndicatorActions";
import { formatIndicatorLabel } from "../../../helpers/indicatorLabel";
import "./PaneHeader.sass";

const ICON_SIZE = 14;

/**
 * Header of one oscillator pane (po-chart-visual-spec.md §5.3): ▾ folds the
 * title, "Name parameters" 👁 ✏️ ✕ in the top left corner, Expand ˄ / ˅ in
 * the top right corner of the pane. A hidden indicator is muted.
 *
 * @param {object}   props
 * @param {object}   props.indicator - engine.getIndicators() item
 * @param {{ top: number, height: number, isExpanded: boolean }} props.pane - top inside the panes area
 * @param {boolean}  props.isFolded - shared by all titles (FuturesChart)
 * @param {() => void} props.onToggleFolded
 * @param {(key: string, isVisible: boolean) => void} props.onSetVisible
 * @param {(key: string) => void} [props.onEdit] - ✏️ is disabled without it
 * @param {(key: string) => void} props.onRemove
 * @param {(key: string, isExpanded: boolean) => void} props.onSetExpanded
 */
const PaneHeader = ({ indicator, pane, isFolded, onToggleFolded, onSetVisible, onEdit, onRemove, onSetExpanded }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const { key, isVisible } = indicator;
  const label = formatIndicatorLabel(indicator);
  const foldLabel = t(isFolded ? "futures.chart.indicators.header_show" : "futures.chart.indicators.header_hide");
  const expandLabel = t(pane.isExpanded ? "futures.chart.indicators.pane_restore" : "futures.chart.indicators.pane_expand");
  const FoldIcon = isFolded ? ChevronRight : ChevronDown;
  const ExpandIcon = pane.isExpanded ? ChevronDown : ChevronUp;

  return (
    <div
      className={`pane-header${isVisible ? "" : " pane-header--hidden"}`}
      style={{ top: pane.top, height: pane.height }}
    >
      <div className="pane-header__title">
        <button
          type="button"
          className="pane-header__btn pane-header__btn--fold"
          aria-label={`${foldLabel} ${label}`}
          aria-expanded={!isFolded}
          title={foldLabel}
          onClick={onToggleFolded}
        >
          <FoldIcon size={ICON_SIZE} aria-hidden="true" />
        </button>
        {!isFolded && (
          <>
            <span className="pane-header__label" title={label}>
              {label}
            </span>
            <IndicatorActions
              label={label}
              isVisible={isVisible}
              onToggleVisible={() => onSetVisible(key, !isVisible)}
              onEdit={onEdit && (() => onEdit(key))}
              onRemove={() => onRemove(key)}
            />
          </>
        )}
      </div>
      <button
        type="button"
        className="pane-header__btn pane-header__btn--expand"
        aria-label={`${expandLabel} ${label}`}
        aria-pressed={pane.isExpanded}
        title={expandLabel}
        onClick={() => onSetExpanded(key, !pane.isExpanded)}
      >
        <ExpandIcon size={ICON_SIZE} aria-hidden="true" />
      </button>
    </div>
  );
};

export default PaneHeader;
